import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { isAfter } from 'date-fns';
import axios from 'cccisd-axios';
import Table from 'cccisd-table';
import Tooltip from 'cccisd-tooltip';
import Modal from 'cccisd-modal';
import StudentContacts from '../../components/StudentContacts';
import MessageForm from '../../components/Messages/Form';
import TakeAssessment from './TakeAssessment';
import Notes from '../../components/Notes';
import { getStudentContactInfo, updateSessionDeploymentInfo, formatDate } from '../../helpers';
import { getReporterProgress } from 'js/selectors/assessments';
import style from './style.css';
import IconInvite from 'cccisd-icons/paperplane';
import _find from 'lodash/find';
import _mapValues from 'lodash/mapValues';

const Boilerplate = window.cccisd.boilerplate;

const AssessmentTable = ({ assessment, setForceReloadQueries }) => {
    const tableRef = useRef(null);
    const reporterProgress = useSelector(state => getReporterProgress(state.app.assessment));

    const getOpenedSessions = sessions => {
        return sessions.filter(session => {
            return session.opensAt && isAfter(new Date(), new Date(session.opensAt));
        });
    };

    const openedSessions = getOpenedSessions(reporterProgress);

    useEffect(() => {
        // Update session_deployment_info for each session in openedSessions
        let ignore = false;
        if (openedSessions.length > 0) {
            const setSessionDeploymentInfo = async () => {
                // Make sure we are modifying the right assessment sessions, parent component may have changed the plan
                if (!ignore && assessment.assignmentPlanId === openedSessions[0].assignmentPlanId) {
                    for await (const session of openedSessions) {
                        // If session has no pawnId, skip because no one has been invited yet
                        if (session.pawn.pawnId && !session.sessionDeploymentInfo) {
                            await updateSessionDeploymentInfo(
                                [
                                    {
                                        contactType: session.reporter,
                                        email: session.email,
                                        pawnId: session.pawn.pawnId,
                                        respondentHash: session.pawn.respondentHash,
                                    },
                                ],
                                session
                            ).then(res => {
                                session.sessionDeploymentInfo = {
                                    ...session.sessionDeploymentInfo,
                                    [session.email]: res[0],
                                };
                            });
                            // If there is a pawnId and sessionDeploymentInfo, check if the pawnId is in the sessionDeploymentInfo
                        } else if (session.pawn.pawnId && session.sessionDeploymentInfo) {
                            const pawnIdsInSessionDeploymentInfo = Object.values(
                                _mapValues(session.sessionDeploymentInfo, o => Number(o.pawnId))
                            );
                            if (!pawnIdsInSessionDeploymentInfo.includes(session.pawn.pawnId)) {
                                await updateSessionDeploymentInfo(
                                    [
                                        {
                                            contactType: session.reporter,
                                            email: session.email,
                                            pawnId: session.pawn.pawnId,
                                            respondentHash: session.pawn.respondentHash,
                                        },
                                    ],
                                    session
                                ).then(res => {
                                    session.sessionDeploymentInfo = {
                                        ...session.sessionDeploymentInfo,
                                        [session.email]: res[0],
                                    };
                                });
                            }
                        }
                    }
                }
            };

            setSessionDeploymentInfo();
        }
        return () => {
            ignore = true;
        };
    }, [openedSessions, assessment]);

    const handleCancelTimepoint = async (row, contactSessionDeploymentInfo) => {
        if (contactSessionDeploymentInfo.isCancelled) {
            contactSessionDeploymentInfo.isCancelled = !contactSessionDeploymentInfo.isCancelled;
        } else {
            contactSessionDeploymentInfo.isCancelled = true;
        }

        try {
            await axios.put(
                window.cccisd.boilerplate.route('api.assignment.session.update', {
                    session: row.sessionId,
                }),
                {
                    sessionDeploymentInfo: row.sessionDeploymentInfo,
                }
            );
        } catch (e) {
            console.error(e);
        }

        setForceReloadQueries();
        return contactSessionDeploymentInfo;
    };

    const columns = [
        {
            name: 'studentId',
            label: 'Student ID',
            sort: true,
            filter: true,
        },
        {
            name: 'notes',
            label: 'Student Notes',
            render: (v, row) => {
                if (row.reporter !== 'Student') {
                    return null;
                }
                return (
                    <div style={{ textAlign: 'center' }}>
                        <Notes pawnId={row.pawn.pawnId} />
                    </div>
                );
            },
        },
        {
            name: 'reporter',
            label: 'Reporter',
            sort: true,
            filter: true,
            tooltip: (
                <>
                    The Reporter is the individual completing the assessment. If an assessment is for the student, this
                    column will say {`'Student'`}. If the student's caregiver is completing the assessment about the
                    student, it will say {`'Caregiver'`}.
                </>
            ),
        },
        {
            name: 'name',
            label: 'Reporter Name',
            sort: true,
            filter: true,
        },
        {
            name: 'invited',
            label: 'Invited',
            sort: true,
            filter: true,
            render: (value, row) => {
                if (!row.isOpen) {
                    return null;
                }

                if (!row.pawn.pawnId) {
                    const studentContactInfo = getStudentContactInfo(assessment, row.studentContactInfo, [
                        row.reporter,
                    ]);
                    return (
                        <div style={{ textAlign: 'center' }}>
                            <Modal
                                trigger={
                                    <button type="button" className="btn btn-danger btn-xs">
                                        <IconInvite /> Invite
                                    </button>
                                }
                                size="large"
                                beforeClose={() => setForceReloadQueries()}
                                render={({ closeModal }) => {
                                    return (
                                        <StudentContacts
                                            closeStudentContactsModal={closeModal}
                                            value={value}
                                            row={row.studentContactInfo}
                                            plan={assessment}
                                            loadData={() => {}} // For backwards compatibility
                                            contactTypes={[row.reporter]}
                                            otherPlanContacts={studentContactInfo.otherPlanContacts}
                                            options={studentContactInfo.options}
                                            session={{
                                                sessionId: row.sessionId,
                                                deploymentId: row.deploymentId,
                                                sessionDeploymentInfo: row.sessionDeploymentInfo,
                                                settings: row.settings,
                                                planLabel: row.planLabel,
                                            }}
                                        />
                                    );
                                }}
                            />
                        </div>
                    );
                }
                return (
                    <div style={{ textAlign: 'center' }}>{value[0] ? formatDate(new Date(value[0].sentAt)) : null}</div>
                );
            },
        },
        {
            name: 'timepoint',
            label: 'Timepoint',
            sort: true,
            filter: true,
        },
        {
            name: 'closesAt',
            label: 'Closes On',
            sort: true,
            filter: true,
            render: (value, row) => {
                if (!row.isOpen) {
                    return (
                        <div style={{ textAlign: 'center', backgroundColor: '#ffdfdf' }}>
                            <div>{formatDate(value)}</div>
                            <div style={{ color: '#b30' }}>Closed</div>
                        </div>
                    );
                }
                return <div style={{ textAlign: 'center' }}>{formatDate(value)}</div>;
            },
        },
        {
            name: 'progress',
            label: 'Progress',
            sort: true,
            render: value => {
                return (
                    <div style={{ textAlign: 'center' }}>
                        {value.completed}/{value.total}
                    </div>
                );
            },
        },
        {
            name: 'completed',
            label: 'Completed',
            sort: true,
            render: (value, row) => {
                const contactSessionDeploymentInfo = _find(row.sessionDeploymentInfo, ['pawnId', row.pawn.pawnId]);
                if (contactSessionDeploymentInfo?.isCancelled) {
                    return null;
                }

                // check if the row has any invite messages and deploymentId matches the session deployment id
                const inviteSent = row.invited?.some(
                    invite => invite?.deploymentId === contactSessionDeploymentInfo?.deploymentId
                );

                if (!value && row.pawn.pawnId && row.isOpen) {
                    return (
                        <div style={{ textAlign: 'center' }}>
                            <Modal
                                trigger={
                                    <button
                                        type="button"
                                        className={inviteSent ? 'btn btn-warning btn-xs' : 'btn btn-danger btn-xs'}
                                    >
                                        <IconInvite /> {inviteSent ? 'Remind' : 'Invite'}
                                    </button>
                                }
                                render={({ closeModal }) => {
                                    const label = row.planLabel;
                                    let currentSessionDeploymentInfo = { pawnId: null, deploymentId: null };
                                    if (row.sessionDeploymentInfo) {
                                        /* eslint-disable eqeqeq  */
                                        currentSessionDeploymentInfo = _find(
                                            Object.values(row.sessionDeploymentInfo),
                                            o => o.pawnId == row.pawn.pawnId
                                        ); // non-strict comparison for comparing string to int
                                    }
                                    const link = Boilerplate.url(
                                        `/sessionPlayer/${currentSessionDeploymentInfo?.deploymentId}/${
                                            assessment.assignmentPlanId
                                        }/${row.sessionId}/${row.settings.reporter}/${row.pawn.pawnId}/${
                                            row.pawn.pawnHash
                                        }?s=${row.settings.options.join(',')}`
                                    );
                                    return (
                                        <MessageForm
                                            sessionDeploymentInfo={currentSessionDeploymentInfo}
                                            contact={{ fields: { contactEmail: row.email } }}
                                            row={{ label }}
                                            label={label}
                                            closeModal={closeModal}
                                            messageSuccess={() => {}}
                                            link={link}
                                            action={inviteSent ? 'assessment_plan_remind' : 'assessment_plan_invite'}
                                        />
                                    );
                                }}
                            />
                        </div>
                    );
                }

                return <div style={{ textAlign: 'center' }}>{formatDate(value)}</div>;
            },
        },
        {
            name: 'actions',
            label: 'Actions',
            render: (value, row) => {
                const contactSessionDeploymentInfo = _find(row.sessionDeploymentInfo, ['pawnId', row.pawn.pawnId]);
                const restoreDescription = 'Restore timepoint administration actions and data collection';
                const cancelDescription =
                    "Canceling a timepoint will prevent data from being collected for this reporter's timepoint. Data that has already been collected will remain in the system";
                return (
                    <div>
                        {contactSessionDeploymentInfo?.isCancelled && <div className={style.badge}>Cancelled</div>}
                        <div style={{ textAlign: 'center', display: 'flex', gap: '0.2em' }}>
                            <Tooltip
                                title={
                                    contactSessionDeploymentInfo?.isCancelled ? restoreDescription : cancelDescription
                                }
                            >
                                <Modal
                                    trigger={
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-xs"
                                            disabled={!row.pawn.pawnId || row.completed || !row.isOpen}
                                            aria-disabled={!row.pawn.pawnId || row.completed || !row.isOpen}
                                        >
                                            <>{contactSessionDeploymentInfo?.isCancelled ? 'Restore' : 'Cancel'}</>
                                        </button>
                                    }
                                    triggerDisabled={!row.pawn.pawnId || row.completed || !row.isOpen}
                                    title={
                                        contactSessionDeploymentInfo?.isCancelled
                                            ? `Restore this timepoint?`
                                            : `Cancel this timepoint?`
                                    }
                                    render={({ closeModal }) => {
                                        return (
                                            <div>
                                                <p>
                                                    {contactSessionDeploymentInfo?.isCancelled
                                                        ? restoreDescription
                                                        : cancelDescription}
                                                </p>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-end',
                                                        gap: '0.5em',
                                                    }}
                                                >
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={closeModal}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        onClick={() => {
                                                            handleCancelTimepoint(
                                                                row,
                                                                contactSessionDeploymentInfo
                                                            ).then(() => closeModal());
                                                        }}
                                                    >
                                                        Confirm
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    }}
                                />
                            </Tooltip>
                            <Modal
                                trigger={
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-xs"
                                        disabled={
                                            !row.pawn.pawnId ||
                                            row.completed ||
                                            !row.isOpen ||
                                            contactSessionDeploymentInfo?.isCancelled
                                        }
                                        aria-disabled={
                                            !row.pawn.pawnId ||
                                            row.completed ||
                                            !row.isOpen ||
                                            contactSessionDeploymentInfo?.isCancelled
                                        }
                                    >
                                        Take
                                    </button>
                                }
                                triggerDisabled={
                                    !row.pawn.pawnId ||
                                    row.completed ||
                                    !row.isOpen ||
                                    contactSessionDeploymentInfo?.isCancelled
                                }
                                size="large"
                                beforeClose={() => setForceReloadQueries()}
                                render={({ closeModal }) => (
                                    <TakeAssessment
                                        assessment={assessment}
                                        pawn={{ ...row.pawn, name: row.name }}
                                        session={{
                                            sessionDeploymentInfo: row.sessionDeploymentInfo,
                                            settings: row.settings,
                                            sessionId: row.sessionId,
                                        }}
                                        closeModal={closeModal}
                                    />
                                )}
                            />
                        </div>
                    </div>
                );
            },
        },
    ];

    if (!assessment) {
        return null;
    }

    return (
        <div className={style.assessmentsTable}>
            <Table data={openedSessions} ref={tableRef} perPage={10} saveState={false} columns={columns} />
        </div>
    );
};

export default AssessmentTable;
